import {
  DashboardState,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  LeftPanelType,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { shouldShowScreenListSkeleton } from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSkeleton from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  getActiveProcessEditorTab,
  requestProcessEditorTab,
  subscribeActiveProcessEditorTab
} from "../../flows/process-editor/process-editor-tab-bus";
import {
  isDiagramTab,
  PROCESS_EDITOR_TABS,
  PROCESS_TAB_DIAGRAM,
  processEditorTabTestId
} from "../../flows/process-editor/process-editor-tabs";
import { ProcessSwitcher } from "../../flows/process-editor/process-switcher";
import { FooterBreadCrumb } from "../../footer/breadcrumb/breadcrumb";
import { DeviceMenu } from "../toolbar-right/devices/device-menu";
import { ReportSizesDropdown } from "../toolbar-right/devices/report-sizes-dropdown";
import { IsometricToggle } from "../toolbar-right/isometric-toggle";
import { UndoRedo } from "../toolbar-right/undo-redo";
import "./design-sub-bar.scss";
import { SubBarScreenSelector } from "./sub-bar-screen-selector";
import { WorkflowUndoRedo } from "./workflow-undo-redo";

export interface IDesignSubBarProps {}

export interface IDesignSubBarState {
  /** Which process-editor tab is open; only the diagram one is rendered beside a right panel. */
  activeProcessTab: string;
}

/**
 * The workflow's tab strip (Diagram / Parameters / …), driven through the process-editor tab
 * bus. The active tab is owned by `process-editor`, which renders the tab bodies — reading it
 * from the bus rather than lifting the state up here keeps that ownership, and keeps the
 * existing "Go to Parameters" links working, since they already drive the same bus.
 * Carried over from KOA-15840's SecondaryToolbar when row-2 became this sub-bar.
 */
const WorkflowTabs = (): React.ReactElement => {
  const [activeTab, setActiveTab] = useState<string>(getActiveProcessEditorTab() ?? PROCESS_TAB_DIAGRAM);

  useEffect((): (() => void) => subscribeActiveProcessEditorTab(setActiveTab), []);

  return (
    <KuikaTabs
      className="kuika-subbar__workflow-tabs"
      size="lg"
      // Labels double as values, so each option is spelled out here only to carry its test-id.
      options={PROCESS_EDITOR_TABS.map((tab: string) => ({
        value: tab,
        label: tab,
        dataTestId: processEditorTabTestId(tab)
      }))}
      value={activeTab}
      onChange={requestProcessEditorTab}
      dataTestId="processEditorTabs"
    />
  );
};

/**
 * Stand-in for the screen selector and breadcrumb while the screen list is still loading.
 * Both controls read from data that arrives partway through openProject, so without it the
 * strip sits visibly empty next to a populated toolbar.
 */
export const SubBarSkeleton = (): React.ReactElement => (
  <div className="kuika-subbar__skeleton" aria-hidden="true" data-testid="subBarSkeleton">
    <KuikaSkeleton width={140} height={20} />
    <KuikaSkeleton width={90} height={12} />
  </div>
);

/**
 * Row-2 of the designer top chrome: the screen/report selector + component breadcrumb on the
 * left (Design and Report Designer alike), undo/redo on the right, and — on Report Designer —
 * the isometric/page-size group beside them. Workflow uses the strip as the process editor's
 * own header (switcher left, tab strip centred). Sits directly beneath the toolbar row.
 */
class DesignSubBar extends PureSdComponent<IDesignSubBarProps & IWithSDProps, IDesignSubBarState> {
  private unsubscribeProcessTab: (() => void) | undefined;

  constructor(props: IDesignSubBarProps & IWithSDProps) {
    super(props);
    this.state = { activeProcessTab: getActiveProcessEditorTab() ?? PROCESS_TAB_DIAGRAM };
  }

  // The tab lives on the process-editor bus, not on `sd`, so nothing here would otherwise
  // re-run when it changes — and `checkNeedRender` deliberately keeps this strip out of most
  // sd updates. The centre hold reads the tab, so it has to follow it.
  componentDidMount = (): void => {
    this.unsubscribeProcessTab = subscribeActiveProcessEditorTab((tab: string): void =>
      this.setState({ activeProcessTab: tab })
    );
  };

  componentWillUnmount = (): void => {
    this.unsubscribeProcessTab?.();
  };

  /** Leaves the open report for the tool's landing page; the report itself stays loaded. */
  handleBackToReportList = (): void => {
    this.props.sd.setVisibilityOfCreateReportDesignPage(true);
  };

  /**
   * Leaves the open process for the Process Automation landing page. Unlike the report one,
   * the selection has to go with it: the gallery's right panel and this very strip are both
   * gated on "no workflow selected", so keeping `selectedWf` would leave the BPMN properties
   * panel standing over the list. The left panel goes back to the process list for the same
   * reason — it would otherwise show the fields of a process no longer open.
   */
  handleBackToProcessList = (): void => {
    this.props.sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
    this.props.sd.setCreatingWfActive(true);
    void this.props.sd.clearSelectedWf();
  };

  /**
   * The way back to a tool's landing page. It matters more on these two than on UI Design:
   * their landing hides behind the open document, so once one opens the only route back was
   * the panel's "+", which reads as "new" rather than "the list I came from".
   */
  renderBackButton = (label: string, dataTestId: string, onClick: () => void): React.ReactElement => (
    <KuikaTooltip position="bottom" content={label}>
      {/* KuikaButton is a forwardRef, but the tooltip hands its ref to a real element either
          way; the span also keeps the trigger out of the left group's shrink. */}
      <span className="kuika-subbar__back">
        <KuikaButton
          type="tertiary"
          size="sm"
          isIcon
          color="neutral"
          aria-label={label}
          data-testid={dataTestId}
          onClick={onClick}
        >
          <KuikaSvg type="arrows" name="arrow-left" width={16} height={16} />
        </KuikaButton>
      </span>
    </KuikaTooltip>
  );

  render() {
    const { sd } = this.props;
    const isDesign: boolean = sd.dashboardState === DashboardState.design;
    const isReportDesigner: boolean = sd.dashboardState === DashboardState.reportDesigner;
    // KOA-15840 extracted the process editor's own header into the shell band, so on the
    // workflow dashboard this strip IS the editor's header: switcher left, tab strip centred.
    const isWorkflow: boolean = sd.dashboardState === DashboardState.workFlow;
    // Workflow's on-demand panels shorten this strip from BOTH sides: the datasources panel
    // pushes its start right, and since KOA-18354 (row-2 no longer spans the right panel) the
    // BPMN properties panel takes width off its end. The tab strip is the primary navigation
    // there and must hold its viewport position — only the process switcher gives way — so each
    // open panel contributes half its width as a counter-shift (see design-sub-bar.scss).
    // Each is gated on the panel being VISIBLE rather than merely configured, because the width
    // stores keep their last value after a close; a collapsed panel unmounts, so visibility is
    // the whole test.
    const holdCentreForLeftPanel: boolean = isWorkflow && sd.getVisibility(ViewPart.LeftPanel) === Visibility.Visible;
    // The BPMN properties panel is the only right panel the process editor renders, and it
    // renders on the diagram tab alone (design-right-panel gates on the same test). Visibility
    // says the panel is configured open, not that it is on screen, so without the tab the hold
    // stayed on across Parameters / Forms / Version History and dragged the strip 146px.
    const holdCentreForRightPanel: boolean =
      isWorkflow &&
      sd.getVisibility(ViewPart.RightPanel) === Visibility.Visible &&
      isDiagramTab(this.state.activeProcessTab);
    // Report Designer drives its own page list through the same selector — but only once a
    // report is actually open. Entering the dashboard leaves the previously edited UI screen
    // loaded (sdId still points at it), and the selector would present that screen as the
    // "current report". getScreenType() resolves the loaded sdId against the screen list —
    // the same source the selector's own tree uses, so the two can't disagree.
    const isReportOpen: boolean = sd.getScreenType() === EScreenType.Report;
    // Screen selector, component breadcrumb and undo/redo all share this gate: they act on the
    // OPEN design, and on Report Designer that is only meaningful once a report is loaded.
    const showScreenSurfaceTools: boolean = isDesign || (isReportDesigner && isReportOpen);
    // The create-report page hides this whole strip (designer-dashboard's showSubBar), so no
    // create-page guard is needed here.
    const showReportSizes: boolean =
      isReportDesigner && sd.getVisibility(ViewPart.ReportSizesDropdown) === Visibility.Visible;
    // Undo/redo act on the edited screen, so they live in this row-2 (screen-design surfaces only)
    // rather than the project-level top bar — moved here from ToolbarRight.
    // See first-load-skeletons for why this is not simply `!isProjectLoaded`.
    const showSelectorSkeleton: boolean = shouldShowScreenListSkeleton(sd);
    const showUndoRedo: boolean =
      showScreenSurfaceTools &&
      KMainFunctions.hasUserAccessInWorkspaceByProjectRole(EUserAccessPart.CreateComponent, sd.project?.projectAccess);

    return (
      <div
        className={classNames("kuika-subbar", {
          "kuika-subbar--hold-left": holdCentreForLeftPanel,
          "kuika-subbar--hold-right": holdCentreForRightPanel
        })}
        data-testid="designSubBar"
      >
        <div className="kuika-subbar__left">
          {/* Inside the showScreenSurfaceTools branch, not beside it: on Report Designer that
              gate resolves against the screen list, so during a load it is false and the real
              controls never appear — a placeholder outside the branch would stand where
              nothing is coming. */}
          {showScreenSurfaceTools &&
            (showSelectorSkeleton ? (
              <SubBarSkeleton />
            ) : (
              <>
                {isReportDesigner &&
                  this.renderBackButton(
                    "Back to Report Designer",
                    "subBarBackToReportsButton",
                    this.handleBackToReportList
                  )}
                <SubBarScreenSelector />
                {/* Component-tree path — appears after the screen/report selector once an
                    element is selected. The breadcrumb self-guards (empty with nothing
                    selected). */}
                <FooterBreadCrumb hideProjectPrefix />
              </>
            ))}
          {isWorkflow && (
            <>
              {this.renderBackButton(
                "Back to Process Automation",
                "subBarBackToProcessesButton",
                this.handleBackToProcessList
              )}
              <ProcessSwitcher />
            </>
          )}
        </div>
        {isWorkflow && (
          <div className="kuika-subbar__center">
            <WorkflowTabs />
          </div>
        )}
        <div className="kuika-subbar__right">
          {showUndoRedo && <UndoRedo />}
          {/* Its own control, not the screen one under a wider gate: `sd.undo()` rewinds the
              IScreenDesign tree and a process is not in that store — see workflow-undo-redo. */}
          {isWorkflow && <WorkflowUndoRedo />}
          {/* UI Design ONLY: a screen has a viewport to switch, a report is sized by paper and
              the workflow diagram has no viewport at all — so neither of those gets the device
              selector (it also gates itself on ViewPart.Devices). */}
          {isDesign && <DeviceMenu />}
          {/* Report Designer's canvas controls sit right of undo/redo (they belong to the same
              "acts on the open design" cluster). The isometric-grid toggle and the page-size
              dropdown read as one control, so they share a bordered group. */}
          {showReportSizes && (
            <div className="kuika-subbar__report-tools">
              <IsometricToggle />
              <ReportSizesDropdown />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading = a screen/report switch: on Report Designer that's what flips the selector and
  // undo/redo between hidden (stale UI screen) and shown (report open). LoadProject and
  // ScreenReload already bypass checkNeedRender in PureSdComponent.
  checkNeedRender = (changeReason: ESdChangeReason): boolean => changeReason === ESdChangeReason.Loading;
}

const designSubBar = withSD<IDesignSubBarProps>(DesignSubBar);
export { designSubBar as DesignSubBar };
